import { api } from '@/lib/api/client';
import type { JournalResponse } from '@/lib/api/types';

/**
 * The integration monitor. **ADMIN only**, reads and writes alike.
 *
 * Three resources, three questions:
 * - `/sources`   — is each feed alive, and how is it configured? One call, whole screen.
 * - `/events`    — what did the feeds do to the schedule, and what went wrong?
 * - `/messages`  — what exactly arrived, payload included?
 *
 * Unlike the dictionaries there is no envelope anywhere: reads return the raw shape,
 * and the PUT returns the saved source directly — a failure here is a real non-200,
 * not a `successfull: false` body.
 */

export const EVENT_TYPES = [
  'CREATED',
  'UPDATED',
  'DELETED',
  'RECEIVED',
  'WARNING',
  'ERROR',
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

/**
 * The traffic light. **Render it, never re-derive it** — the run/success/error marks
 * it is computed from are not exposed any more, precisely so nobody recomputes it
 * differently. `UNKNOWN` means "never delivered", not "unknown source".
 */
export const HEALTH_VALUES = ['OK', 'STALE', 'FAILING', 'UNKNOWN'] as const;
export type Health = (typeof HEALTH_VALUES)[number];

export const FLIGHT_LEGS = ['ARRIVAL', 'DEPARTURE'] as const;
export type FlightLeg = (typeof FLIGHT_LEGS)[number];

/**
 * The schedule columns an event can report a change to, as `changes[].field` names
 * them — and the values `changedField` filters on.
 *
 * The same closed list `monitor/changes.ts` maps to labels; it lives there because
 * that is where it is read, and here because that is where it is sent.
 */
export const CHANGEABLE_FIELDS = [
  'scheduledDate',
  'estimatedDate',
  'actualDate',
  'departedTime',
  'flightStatus',
  'delayMinutes',
  'delayReason',
  'passengerAmount',
  'totalCargoWeight',
  'planeParkingName',
  'externalFlightId',
  'active',
] as const;

export type ChangeableField = (typeof CHANGEABLE_FIELDS)[number];

export interface IntegrationSourceInfo {
  /**
   * The one name for the source. Addresses the PUT, and is read-only forever.
   *
   * A plain `string`. This was once a union of codes hardcoded in this file, which
   * is a claim about the response the server does not honour — it can return a
   * source the build has never heard of, and the union made that unrepresentable,
   * so TypeScript let a value through that the URL reader then rejected at runtime.
   * A response is evidence, not a promise.
   */
  sourceCode: string;
  /** The editable human label; fall back to the code while it is null. */
  displayName: string | null;
  /** Off = the source writes NOTHING and is never judged stale. */
  enabled: boolean;
  /** null = a push feed: nobody polls it, so staleness is not evaluated. */
  pollIntervalSeconds: number | null;
  /** What "reset to default" submits. */
  defaultPollIntervalSeconds: number | null;
  /** false = never saved; everything shown is a default. The first save creates the row. */
  configured: boolean;
  /** 0–100, default 50. Higher overwrites lower; equal ranks = last writer wins. */
  trustRank: number;
  updatedBy: string | null;
  updated: string | null;
  /** A whole-archive re-read is running. Started by PUTting `replayRequested`. */
  replayPending?: boolean;
  /** The last archive file that re-read finished. */
  replayProgress?: string | null;
  health: Health;
}

/** One changed field, `null -> value` on CREATED rows. */
export interface FieldChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface IntegrationEventInfo {
  id: number;
  /**
   * Epoch MILLISECONDS, a JSON number — not the ISO string the older documentation
   * showed. `new Date(created)` is correct; anything that assumed a string parsed
   * whatever `new Date("…")` made of it.
   */
  created: number;
  /** null only on mail that matched no message type — render as unknown. */
  sourceCode: string | null;
  eventType: EventType;
  flightNumber: string | null;
  airportIataCode: string | null;
  flightType: FlightLeg | null;
  /** The online-table row this touched. Survives the row: there is no foreign key. */
  airportScheduleId: number | null;
  details: string | null;
  changes: FieldChange[] | null;
  messageRef: string | null;
  /** The delivery behind the event — `fetchMessage(messageId)` returns its payload. */
  messageId: number | null;
  /**
   * **Whether the source's record of this flight exists at all.**
   *
   * Read THIS to decide whether to offer a "view source" control — never the
   * nullness of `rawData`, which is null in every list response by design because
   * the list query does not select the column. The two say different things, and
   * conflating them makes a working feed look like it lost data: an event minutes
   * old with no record is a source that has none to give, not one that was cleared
   * after fourteen days.
   */
  hasRawData: boolean;
  /**
   * The source's own words about THIS flight — the `<FLIGHT>` element, the CSV
   * line, the scraped row, the telex body. On an `ERROR` or `WARNING` it is the body
   * that failed instead.
   *
   * `null` in a list unless `includeRawData` was asked for; `fetchEvent(id)` always
   * carries it. Truncated at 20 000 characters and cleared after 14 days.
   */
  rawData: string | null;
}

export type MessageStatus = 'PROCESSED' | 'UNCHANGED' | 'FAILED';

export interface IntegrationMessageInfo {
  id: number;
  /** Epoch milliseconds, a JSON number — as on events. */
  created: number;
  sourceCode: string | null;
  messageType: string | null;
  externalRef: string | null;
  /**
   * SHA-256 of the content — how "same delivery again?" is decided. `null` where the
   * source offered no content to compare.
   */
  payloadChecksum: string | null;
  /**
   * **Whether this delivery kept its content.**
   *
   * `false` is a real and ordinary answer, not a failure: a successful airport
   * snapshot deliberately keeps no file, because the per-flight records on the
   * events are the useful half and the 1.5 MB file was not. Nothing will open at any
   * endpoint — go to the flight's event instead.
   */
  hasRawPayload: boolean;
  /** Always null in a LIST; `fetchMessage(id)` carries it when `hasRawPayload`. */
  rawPayload: string | null;
  /** `N / 0` is meaningful: the file differed, but nothing needed writing. */
  rowsReceived: number | null;
  rowsProcessed: number | null;
  /** UNCHANGED is legacy: such polls are no longer stored at all. */
  status: MessageStatus;
  errorText: string | null;
}

/** Every source, always — including ones that have never delivered (`UNKNOWN`). */
export const fetchSources = (signal?: AbortSignal): Promise<IntegrationSourceInfo[]> =>
  api
    .get<IntegrationSourceInfo[]>('/integration-monitor/sources', undefined, signal)
    .then((response) => (Array.isArray(response) ? response : []));

/**
 * The PUT body. **Applied whole, not field-by-field** — every field the form shows is
 * sent, including unchanged ones, because `pollIntervalSeconds: null` is a meaningful
 * value ("do not judge staleness") and an omitted field therefore cannot mean "keep".
 *
 * `replayRequested` is a command, not a setting: true starts a whole-archive re-read,
 * false cancels one, absent leaves a running one alone. Only KAZ_NAVIGATION
 * implements it today; the rest ignore the flag.
 */
export interface SaveSourceBody {
  displayName: string | null;
  enabled: boolean;
  pollIntervalSeconds: number | null;
  trustRank: number;
  replayRequested?: boolean;
}

/**
 * Returns the saved source, live state included — no follow-up GET needed.
 *
 * `code` is a `string` for the same reason the response field is: it comes from a
 * row the server sent, and refusing to address a source this build does not know
 * would leave it unconfigurable. The path is the server's own identifier either way.
 */
export const saveSource = (
  code: string,
  body: SaveSourceBody,
): Promise<IntegrationSourceInfo> =>
  api.put<IntegrationSourceInfo>(`/integration-monitor/sources/${code}`, body);

/**
 * The event journal's filter. Everything optional; blanks are dropped by the client.
 *
 * No `createdFrom`: the server windows an unfiltered feed to the **last 48 hours**,
 * and the two targeted filters — a flight number or a schedule row — are never
 * windowed, because asking for one flight's history means all of it.
 */
export interface EventCriteria {
  /** A code from `/integration-monitor/sources`. The server 400s on an unknown
   *  one, which `useMonitor` reports as a rejected filter. */
  sourceCode?: string;
  eventType?: EventType | '';
  flightType?: FlightLeg | '';
  /** Only events touching this column. */
  changedField?: ChangeableField | '';
  /** Exact, case-insensitive. **Not windowed** — returns the flight's whole life. */
  flightNumber?: string;
  /** One online-table row's whole history. **Not windowed.** */
  airportScheduleId?: number;
  /** `yyyy-MM-dd'T'HH:mm:ss`. Absent means the server's own last-48-hours window. */
  createdFrom?: string;
  createdTo?: string;
  pageNum: number;
  rows: number;
}

export const fetchEvents = (
  criteria: EventCriteria,
  signal?: AbortSignal,
): Promise<JournalResponse<IntegrationEventInfo>> =>
  api.get<JournalResponse<IntegrationEventInfo>>(
    '/integration-monitor/events',
    // Written out rather than cast: a rename on either side then breaks the build
    // instead of silently sending a parameter the server rejects with a 400.
    {
      sourceCode: criteria.sourceCode,
      eventType: criteria.eventType,
      flightType: criteria.flightType,
      changedField: criteria.changedField,
      flightNumber: criteria.flightNumber,
      airportScheduleId: criteria.airportScheduleId,
      createdFrom: criteria.createdFrom,
      createdTo: criteria.createdTo,
      pageNum: criteria.pageNum,
      rows: criteria.rows,
    },
    signal,
  );

/**
 * One event WITH its `rawData` — what the source said about that one flight.
 *
 * This is the per-row call; `includeRawData` on the list is the same thing for a
 * whole page, opt-in because of the size. A `404` means the 30-day cleanup has
 * already taken the event.
 */
export const fetchEvent = (id: number): Promise<IntegrationEventInfo> =>
  api.get<IntegrationEventInfo>(`/integration-monitor/events/id/${id}`);

export interface MessageCriteria {
  /** A code from `/integration-monitor/sources`. The server 400s on an unknown
   *  one, which `useMonitor` reports as a rejected filter. */
  sourceCode?: string;
  createdFrom?: string;
  createdTo?: string;
  pageNum: number;
  rows: number;
}

export const fetchMessages = (
  criteria: MessageCriteria,
  signal?: AbortSignal,
): Promise<JournalResponse<IntegrationMessageInfo>> =>
  api.get<JournalResponse<IntegrationMessageInfo>>(
    '/integration-monitor/messages',
    {
      sourceCode: criteria.sourceCode,
      createdFrom: criteria.createdFrom,
      createdTo: criteria.createdTo,
      pageNum: criteria.pageNum,
      rows: criteria.rows,
    },
    signal,
  );

/** One delivery WITH its payload. 404 once retention removes the row (60 days). */
export const fetchMessage = (id: number): Promise<IntegrationMessageInfo> =>
  api.get<IntegrationMessageInfo>(`/integration-monitor/messages/id/${id}`);
